import { useCallback, useEffect, useRef, useState } from 'react';

import styles from './ComponentOverlayLayer.module.css';
import { getSectionColor } from './sectionColors';

interface OverlayEntry {
  name: string;
  rect: DOMRect;
  section: string;
}

function unionRects(a: DOMRect, b: DOMRect): DOMRect {
  const left = Math.min(a.left, b.left);
  const top = Math.min(a.top, b.top);
  const right = Math.max(a.right, b.right);
  const bottom = Math.max(a.bottom, b.bottom);
  return new DOMRect(left, top, right - left, bottom - top);
}

// Measure the rendered box of an overlay wrapper. The wrapper is `display:contents` so it never
// affects layout — but that means it generates no box of its own, and in atlas-astro each component
// is *additionally* wrapped in an `<astro-island>`, which Astro also styles `display:contents`.
// `Range.getBoundingClientRect()` does not reliably descend through a `display:contents` member node:
// it happens to work for the single wrapper level in RR7 atlas, but the extra astro-island level in
// atlas-astro yields offset/shrunk rects (NGF-6389). Instead, recurse past every box-less level
// (`display:contents`, not-yet-laid-out, or `display:none` — all report an all-zero rect) and union
// the client rects of the first descendants that actually generate a box.
function getRenderedRect(el: Element): DOMRect | null {
  const rect = el.getBoundingClientRect();
  if (rect.width !== 0 || rect.height !== 0) return rect;

  let union: DOMRect | null = null;
  for (const child of Array.from(el.children)) {
    const childRect = getRenderedRect(child);
    if (!childRect) continue;
    union = union ? unionRects(union, childRect) : childRect;
  }
  return union;
}

function measureOverlays(): OverlayEntry[] {
  const nodes = document.querySelectorAll<HTMLElement>('[data-overlay-name]');
  const entries: OverlayEntry[] = [];

  nodes.forEach(el => {
    const name = el.dataset.overlayName ?? '';
    const section = el.dataset.overlaySection ?? '';

    try {
      const rect = getRenderedRect(el);

      // Skip components that produced no measurable box (hidden or not yet rendered)
      if (!rect) return;

      entries.push({ name, rect, section });
    } catch {
      // Ignore measurement errors for individual components
    }
  });

  return entries;
}

export function ComponentOverlayLayer() {
  const [mounted, setMounted] = useState(false);
  const [overlays, setOverlays] = useState<OverlayEntry[]>([]);
  const rafRef = useRef<number | null>(null);

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setOverlays(measureOverlays());
    });
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    // Initial measurement
    scheduleUpdate();

    // Scroll listener (capture phase, passive)
    window.addEventListener('scroll', scheduleUpdate, { capture: true, passive: true });

    // ResizeObserver on documentElement for viewport/layout changes
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(document.documentElement);

    // Debounced MutationObserver on the dynamic page wrapper to catch async/lazy hydration
    let mutationDebounce: ReturnType<typeof setTimeout> | null = null;
    const debouncedScheduleUpdate = () => {
      if (mutationDebounce !== null) clearTimeout(mutationDebounce);
      mutationDebounce = setTimeout(scheduleUpdate, 100);
    };

    const mutationObserver = new MutationObserver(debouncedScheduleUpdate);
    const wrapper = document.getElementById('dynamic-page-main-tag-wrapper');
    if (wrapper) {
      mutationObserver.observe(wrapper, { childList: true, subtree: true, attributes: false });
    }

    return () => {
      window.removeEventListener('scroll', scheduleUpdate, { capture: true });
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (mutationDebounce !== null) clearTimeout(mutationDebounce);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [mounted, scheduleUpdate]);

  // Render nothing during SSR and initial hydration
  if (!mounted) return null;

  return (
    <div aria-hidden="true" className={styles.overlayLayer}>
      {overlays.map((entry, i) => {
        const color = getSectionColor(entry.section);
        const label = entry.section ? `${entry.name} · ${entry.section}` : entry.name;

        return (
          <div
            className={styles.componentBox}
            key={`${entry.name}-${i}`}
            style={{
              top: entry.rect.top,
              left: entry.rect.left,
              width: entry.rect.width,
              height: entry.rect.height,
              outline: `2px solid ${color}`,
            }}
          >
            <span className={styles.componentLabel} style={{ backgroundColor: color }}>
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
